/**
 * Duplicate detection logic
 */

import type { CslItem } from "../../core/csl-json/types.js";
import { normalize } from "../search/normalizer.js";
import type { DuplicateMatch, DuplicateResult } from "./types.js";

/**
 * Normalize DOI by removing common URL prefixes
 * Returns the DOI in format: 10.xxxx/...
 */
export function normalizeDoi(doi: string): string {
  // Remove common DOI URL prefixes
  const normalized = doi
    .replace(/^https?:\/\/doi\.org\//i, "")
    .replace(/^https?:\/\/dx\.doi\.org\//i, "")
    .replace(/^doi:/i, "");

  return normalized;
}

/**
 * Extract year from CSL-JSON issued field
 */
export function extractYear(item: CslItem): string | null {
  const dateParts = item.issued?.["date-parts"]?.[0];
  if (!dateParts || dateParts.length === 0) {
    return null;
  }
  return String(dateParts[0]);
}

/**
 * Normalize author names to "family given-initial" format
 */
export function normalizeAuthors(item: CslItem): string | null {
  if (!item.author || item.author.length === 0) {
    return null;
  }

  // Combine all authors: "family given-initial"
  const authorStrings = item.author.map((author) => {
    if (author.literal) return author.literal;
    const family = author.family || "";
    const givenInitial = author.given ? author.given.charAt(0) : "";
    return `${family} ${givenInitial}`.trim();
  });

  // Join and normalize
  return normalize(authorStrings.join(" "));
}

/**
 * Check if two items match by DOI
 */
function checkDoiMatch(item: CslItem, existing: CslItem): DuplicateMatch | null {
  if (!item.DOI || !existing.DOI) {
    return null;
  }

  const normalizedItemDoi = normalizeDoi(item.DOI);
  const normalizedExistingDoi = normalizeDoi(existing.DOI);

  // DOI comparison is case-sensitive
  if (normalizedItemDoi === normalizedExistingDoi) {
    return {
      type: "doi",
      existing,
      details: {
        doi: normalizedExistingDoi,
      },
    };
  }

  return null;
}

/**
 * Check if two items match by PMID
 */
function checkPmidMatch(item: CslItem, existing: CslItem): DuplicateMatch | null {
  if (!item.PMID || !existing.PMID) {
    return null;
  }

  // PMID comparison is exact string match
  if (item.PMID === existing.PMID) {
    return {
      type: "pmid",
      existing,
      details: {
        pmid: existing.PMID,
      },
    };
  }

  return null;
}

/**
 * Normalize ISBN by removing hyphens, spaces, and uppercasing X
 */
export function normalizeIsbnForComparison(isbn: string): string {
  return isbn.replace(/[-\s]/g, "").toUpperCase();
}

/**
 * Types that should match by ISBN only (not title)
 */
const BOOK_TYPES = ["book"];

/**
 * Types that should match by ISBN + title (chapters in the same book can differ)
 */
export const BOOK_SECTION_TYPES = ["chapter"];

/**
 * Check if two items match by ISBN
 * - For book type: ISBN only
 * - For chapter type: ISBN + title
 */
function checkIsbnMatch(item: CslItem, existing: CslItem): DuplicateMatch | null {
  if (!item.ISBN || !existing.ISBN) {
    return null;
  }

  const normalizedItemIsbn = normalizeIsbnForComparison(item.ISBN);
  const normalizedExistingIsbn = normalizeIsbnForComparison(existing.ISBN);

  if (normalizedItemIsbn !== normalizedExistingIsbn) {
    return null;
  }

  // For book type: ISBN only is enough
  if (BOOK_TYPES.includes(item.type) || BOOK_TYPES.includes(existing.type)) {
    return {
      type: "isbn",
      existing,
      details: {
        isbn: normalizedExistingIsbn,
      },
    };
  }

  // For chapter type: ISBN + title must match
  if (BOOK_SECTION_TYPES.includes(item.type) || BOOK_SECTION_TYPES.includes(existing.type)) {
    const itemTitle = item.title ? normalize(item.title) : null;
    const existingTitle = existing.title ? normalize(existing.title) : null;

    if (itemTitle && existingTitle && itemTitle === existingTitle) {
      return {
        type: "isbn-title",
        existing,
        details: {
          isbn: normalizedExistingIsbn,
          normalizedTitle: existingTitle,
        },
      };
    }
    return null;
  }

  // For other types with ISBN: treat as book
  return {
    type: "isbn",
    existing,
    details: {
      isbn: normalizedExistingIsbn,
    },
  };
}

/**
 * Check for arXiv ID match
 */
function checkArxivMatch(item: CslItem, existing: CslItem): DuplicateMatch | null {
  const itemArxivId = item.custom?.arxiv_id;
  const existingArxivId = existing.custom?.arxiv_id;

  if (!itemArxivId || !existingArxivId) {
    return null;
  }

  // Compare base IDs (strip version suffix for matching)
  const stripVersion = (id: string): string => id.replace(/v\d+$/, "");
  if (stripVersion(itemArxivId) === stripVersion(existingArxivId)) {
    return {
      type: "arxiv",
      existing,
      details: {
        arxiv_id: existingArxivId,
      },
    };
  }

  return null;
}

/**
 * Check for ERIC ID match
 */
function checkEricMatch(item: CslItem, existing: CslItem): DuplicateMatch | null {
  const itemEricId = item.custom?.eric_id;
  const existingEricId = existing.custom?.eric_id;

  if (!itemEricId || !existingEricId) {
    return null;
  }

  // ERIC ID comparison is exact string match
  if (itemEricId === existingEricId) {
    return {
      type: "eric",
      existing,
      details: {
        eric_id: existingEricId,
      },
    };
  }

  return null;
}

/**
 * Check for Scopus ID match
 */
function checkScopusMatch(item: CslItem, existing: CslItem): DuplicateMatch | null {
  const itemScopusId = item.custom?.scopus_id;
  const existingScopusId = existing.custom?.scopus_id;

  if (!itemScopusId || !existingScopusId) {
    return null;
  }

  // Scopus ID comparison is exact string match
  if (itemScopusId === existingScopusId) {
    return {
      type: "scopus",
      existing,
      details: {
        scopus_id: existingScopusId,
      },
    };
  }

  return null;
}

/**
 * Check if two items match by Title + Author + Year
 */
function checkTitleAuthorYearMatch(item: CslItem, existing: CslItem): DuplicateMatch | null {
  const itemTitle = item.title ? normalize(item.title) : null;
  const existingTitle = existing.title ? normalize(existing.title) : null;
  const itemAuthors = normalizeAuthors(item);
  const existingAuthors = normalizeAuthors(existing);
  const itemYear = extractYear(item);
  const existingYear = extractYear(existing);

  // All three must be present and match
  if (
    !itemTitle ||
    !existingTitle ||
    !itemAuthors ||
    !existingAuthors ||
    !itemYear ||
    !existingYear
  ) {
    return null;
  }

  if (itemTitle === existingTitle && itemAuthors === existingAuthors && itemYear === existingYear) {
    return {
      type: "title-author-year",
      existing,
      details: {
        normalizedTitle: existingTitle,
        normalizedAuthors: existingAuthors,
        year: existingYear,
      },
    };
  }

  return null;
}

/**
 * Check if an item is a duplicate of an existing item
 * Returns the first match found (highest priority)
 */
function checkSingleDuplicate(item: CslItem, existing: CslItem): DuplicateMatch | null {
  // Priority 1: DOI matching (highest priority)
  const doiMatch = checkDoiMatch(item, existing);
  if (doiMatch) {
    return doiMatch;
  }

  // Priority 2: PMID matching
  const pmidMatch = checkPmidMatch(item, existing);
  if (pmidMatch) {
    return pmidMatch;
  }

  // Priority 3: ISBN matching
  const isbnMatch = checkIsbnMatch(item, existing);
  if (isbnMatch) {
    return isbnMatch;
  }

  // Priority 4: arXiv ID matching
  const arxivMatch = checkArxivMatch(item, existing);
  if (arxivMatch) {
    return arxivMatch;
  }

  // Priority 5: ERIC ID matching
  const ericMatch = checkEricMatch(item, existing);
  if (ericMatch) {
    return ericMatch;
  }

  // Priority 6: Scopus ID matching
  const scopusMatch = checkScopusMatch(item, existing);
  if (scopusMatch) {
    return scopusMatch;
  }

  // Priority 7: Title + Author + Year matching (lowest priority)
  return checkTitleAuthorYearMatch(item, existing);
}

/**
 * Detects if a reference is a duplicate of any existing references
 *
 * Priority order:
 * 1. DOI (highest priority)
 * 2. PMID
 * 3. ISBN
 * 4. arXiv ID
 * 5. ERIC ID
 * 6. Scopus ID
 * 7. Title + Author + Year (lowest priority)
 *
 * @param item - The reference to check for duplicates
 * @param existingReferences - Array of existing references to check against
 * @returns DuplicateResult indicating if duplicate found and match details
 */
export function detectDuplicate(item: CslItem, existingReferences: CslItem[]): DuplicateResult {
  const matches: DuplicateMatch[] = [];
  const itemUuid = item.custom?.uuid;

  for (const existing of existingReferences) {
    // Skip if same UUID (same item)
    if (itemUuid && existing.custom?.uuid === itemUuid) {
      continue;
    }

    const match = checkSingleDuplicate(item, existing);
    if (match) {
      matches.push(match);
    }
  }

  return {
    isDuplicate: matches.length > 0,
    matches,
  };
}
